// Fixed SpotifySearchModal.js - No memory leaks
import React, { useState, useEffect, useRef } from 'react';
import { usePlayerContext } from '../../hooks/PlayerContext';
import { useSyncOperation, SyncOperations } from '../../hooks/SyncCoordinator';
import logger from '../../services/logger';

const SpotifySearchModal = ({ isOpen, onClose }) => {
  const { 
    state, 
    addTrackToPlaylist, 
    playTrack,
    normalizeTrackFormat
  } = usePlayerContext();
  const { runSync } = useSyncOperation();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('track');
  
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // Cleanup on unmount or close
  useEffect(() => {
    return () => {
      // Clear timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSearchResults([]);
      setLoading(false);
    }
  }, [isOpen]);
  
  // Debounced search with cleanup
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Abort previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    if (!searchTerm.trim() || !state.accessToken) {
      setSearchResults([]);
      return;
    }
    
    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      performSearch();
    }, 500);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, activeTab, state.accessToken]);
  
  const performSearch = async () => {
    await runSync(SyncOperations.SEARCH, async () => {
      setLoading(true);
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      try {
        const response = await fetch(
          `https://api.spotify.com/v1/search?q=${encodeURIComponent(searchTerm)}&type=${activeTab}&limit=10`,
          {
            headers: {
              'Authorization': `Bearer ${state.accessToken}`
            },
            signal: abortControllerRef.current.signal
          }
        );
        
        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Format results
        let formattedResults = [];
        
        if (activeTab === 'track' && data.tracks?.items) {
          formattedResults = data.tracks.items.map(track => ({
            id: track.id,
            uri: track.uri,
            name: track.name,
            artists: track.artists.map(a => a.name).join(', '),
            album: track.album.name,
            images: track.album.images,
            duration_ms: track.duration_ms
          }));
        }
        
        setSearchResults(formattedResults);
      } catch (error) {
        if (error.name !== 'AbortError') {
          logger.error('Search error:', error);
          setSearchResults([]);
        }
      } finally {
        setLoading(false);
      }
    });
  };
  
  // Synchronized actions
  const handlePlayNow = async (track) => {
    await runSync(SyncOperations.TRACK_CHANGE, async () => {
      const normalizedTrack = normalizeTrackFormat(track);
      if (normalizedTrack) {
        playTrack(normalizedTrack);
        onClose();
      }
    });
  };
  
  const handlePlayNext = async (track) => {
    await runSync(SyncOperations.PLAYLIST_UPDATE, async () => {
      const normalizedTrack = normalizeTrackFormat(track);
      if (normalizedTrack) {
        addTrackToPlaylist(normalizedTrack, 'next');
        onClose();
      }
    });
  };
  
  const handleAddToQueue = async (track) => {
    await runSync(SyncOperations.PLAYLIST_UPDATE, async () => {
      const normalizedTrack = normalizeTrackFormat(track);
      if (normalizedTrack) {
        addTrackToPlaylist(normalizedTrack, 'end');
        onClose();
      }
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="search-modal" onClick={(e) => {
      if (e.target.className === 'search-modal') onClose();
    }}>
      <div className="search-modal-content">
        <div className="search-modal-header">
          <h3>Search Music</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="search-input-container">
          <input 
            type="text" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search songs, artists, albums..."
            className="search-input"
            autoFocus
          />
        </div>
        
        <div className="search-tabs">
          <button 
            className={`search-tab ${activeTab === 'track' ? 'active' : ''}`}
            onClick={() => setActiveTab('track')}
          >
            Tracks
          </button>
        </div>
        
        <div className="search-results-container">
          {loading ? (
            <div className="search-loading">
              <div className="loading-spinner"></div>
              <p>Searching...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="search-results-grid">
              {searchResults.map((track) => (
                <div key={track.id} className="media-item track-item">
                  <div className="media-cover-art">
                    <img 
                      src={track.images?.[0]?.url || '/default-album.png'} 
                      alt={track.name} 
                    />
                    <div className="media-play-overlay">
                      <button 
                        className="play-button"
                        onClick={() => handlePlayNow(track)}
                      >▶</button>
                    </div>
                  </div>
                  <div className="media-info">
                    <div className="media-title">{track.name}</div>
                    <div className="media-subtitle">{track.artists}</div>
                    <div className="track-actions">
                      <button className="track-action-button">•••</button>
                      <div className="hover-menu">
                        <button onClick={() => handlePlayNow(track)}>Play Now</button>
                        <button onClick={() => handlePlayNext(track)}>Play Next</button>
                        <button onClick={() => handleAddToQueue(track)}>Add to Queue</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : searchTerm ? (
            <div className="search-no-results">
              <p>No results found for "{searchTerm}"</p>
            </div>
          ) : (
            <div className="search-placeholder">
              <div className="search-icon-large">🎵</div>
              <p>Search for songs to add to your queue</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpotifySearchModal;